import React, { useContext, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ConfigurationContext from "../../context/Configuration";
import { resizedImage } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS } from "../../utils/designTokens";
import ReviewDialog, { StarRating } from "./ReviewDialog";
import { deliveryKind } from "./LiveOrderBanner";
import { itemOption, orderTotal, titleCaseIfShouting } from "./orderText";
import { isInStoreOrder } from "./OrderDetail/orderProgress";
import { formatIn, timeIn, wallParts } from "../../utils/restaurantClock";

/** The chip: Delivered, Picked up, Completed, Cancelled, or a ticket's Refunded / Disputed. */
export function pastStatus(o) {
  if (o.supportTicketStatus === "RESOLVED") return { label: "Refunded", color: C.teal, bg: C.tealSoft };
  if (o.supportTicketStatus === "CLOSED_NO_ACTION") return { label: "Disputed", color: C.gold, bg: C.goldSoft };
  if (o.orderStatus === "CANCELLED") return { label: "Cancelled", color: "#8E1C14", bg: C.surface2, cancelled: true };
  if (o.orderStatus === "COMPLETED" || isInStoreOrder(o)) return { label: "Completed", color: C.green, bg: C.greenSoft };
  if (o.isPickedUp && (o.orderStatus === "DELIVERED" || o.orderStatus === "PICKED")) return { label: "Picked up", color: C.green, bg: C.greenSoft };
  return { label: "Delivered", color: C.green, bg: C.greenSoft };
}

const toDate = (raw) => new Date(/^\d+$/.test(String(raw)) ? parseInt(raw, 10) : raw);

/** "Sat, Sep 18 · 7:42 PM" (with the year when it isn't this one), on the restaurant's clock. */
function whenLabel(raw, tz) {
  if (!raw) return "";
  const d = toDate(raw);
  const sameYear = wallParts(d, tz).y === wallParts(new Date(), tz).y;
  const day = formatIn(d, tz, { weekday: "short", month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
  return `${day} · ${timeIn(d, tz)}`;
}

/**
 * A finished order. The restaurant name is the card's link to order details
 * (stretched over the card); the stars, Reorder and View details sit above
 * it. Stars open the review popup with that rating chosen.
 */
function OrderHistoryCard({ order, onReorder, reordering }) {
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const status = pastStatus(order);
  const [reviewRating, setReviewRating] = useState(order.review?.rating || 0);
  const [reviewed, setReviewed] = useState(Boolean(order.review));
  const [preview, setPreview] = useState(0);
  const [dialogRating, setDialogRating] = useState(0);
  const canReview = !reviewed && !status.cancelled && ["DELIVERED", "COMPLETED", "PICKED"].includes(order.orderStatus);
  const logo = order.restaurant?.logo || order.restaurant?.image;
  const items = order.items || [];
  const more = items.length - 3;

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        minWidth: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: "22px",
        transition: "border-color .15s ease, box-shadow .15s ease",
        "&:hover": { borderColor: C.borderStrong, boxShadow: "0 6px 18px rgba(26,25,24,0.06)" },
      }}
    >
      <Box sx={{ p: "18px", display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: "50%", border: `1px solid ${C.border}`, overflow: "hidden", bgcolor: C.surface, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
          {logo ? (
            <Box component="img" src={resizedImage(logo, 128)} alt="" loading="lazy" decoding="async" sx={{ width: "100%", height: "100%", objectFit: "cover", filter: status.cancelled ? "grayscale(60%)" : "none" }} />
          ) : (
            <RestaurantRoundedIcon aria-hidden="true" />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component={RouterLink}
            to={`/order-detail/${order._id}`}
            sx={{
              display: "block",
              fontFamily: FONT_BODY,
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              textDecoration: "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "22px" },
              "&:focus-visible": { outline: "none" },
              "&:focus-visible::after": { outline: `2px solid ${C.ink}`, outlineOffset: "2px" },
            }}
          >
            {order.restaurant?.name || "Your order"}
          </Box>
          <Box sx={{ mt: 0.25, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{whenLabel(order.createdAt, order.restaurant?.timezone)}</Box>
        </Box>
        <Box component="span" sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 0.75, height: 26, px: 1.25, borderRadius: RADIUS.pill, bgcolor: status.bg, color: status.color, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700 }}>
          <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: status.color }} />
          {status.label}
        </Box>
      </Box>

      {items.length > 0 && (
        <Box component="ul" sx={{ mx: "18px", my: 0, p: "12px 14px", listStyle: "none", borderRadius: "14px", bgcolor: C.surface, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {items.slice(0, 3).map((it, i) => {
            const option = itemOption(it);
            return (
              <Box component="li" key={it._id || i} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, fontFamily: FONT_BODY, fontSize: 14 }}>
                <Box component="span" sx={{ flexShrink: 0, minWidth: 30, height: 22, px: 0.5, boxSizing: "border-box", borderRadius: "7px", bgcolor: C.bg, border: `1px solid ${C.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.ink }}>
                  {it.quantity}×
                </Box>
                <Box component="span" sx={{ minWidth: 0, color: C.ink, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{titleCaseIfShouting(it.title)}</Box>
                {option && <Box component="span" sx={{ flexShrink: 0, color: C.muted, fontSize: 13 }}>{option}</Box>}
              </Box>
            );
          })}
          {more > 0 && <Box component="li" sx={{ fontFamily: FONT_BODY, fontSize: 13, color: C.muted, pl: "38px" }}>+{more} more {more === 1 ? "item" : "items"}</Box>}
        </Box>
      )}

      <Box sx={{ px: "18px", pt: 1.5, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {order.orderId ? `#${order.orderId} · ` : ""}
          {deliveryKind(order)}
        </Box>
        <Box sx={{ flexShrink: 0, fontFamily: FONT_BODY, fontSize: 18, fontWeight: 800, color: status.cancelled ? C.muted : C.ink, textDecoration: status.cancelled ? "line-through" : "none" }}>
          {currency}{orderTotal(order).toFixed(2)}
        </Box>
      </Box>

      {/* Pinned to the bottom, above the card's link. */}
      <Box sx={{ mt: "auto", p: "14px 18px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, position: "relative", zIndex: 1, pointerEvents: "none", "& > *": { pointerEvents: "auto" } }}>
        {status.cancelled ? (
          <Box component={RouterLink} to={`/order-detail/${order._id}`} sx={{ ...FOCUS_RING, display: "inline-flex", alignItems: "center", minHeight: 40, px: 0.5, borderRadius: "8px", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: C.redLink, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
            View details
          </Box>
        ) : reviewed && reviewRating > 0 ? (
          <StarRating value={reviewRating} readOnly size={24} />
        ) : reviewed ? (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: C.green }}>
            <StarRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
            Reviewed
          </Box>
        ) : canReview ? (
          <StarRating
            value={preview}
            size={26}
            label={`Rate your order from ${order.restaurant?.name || "this restaurant"}`}
            onChange={(n, how) => {
              setPreview(n);
              if (how?.keyboard) return;
              setDialogRating(n);
            }}
          />
        ) : (
          <span />
        )}
        {order.items?.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => onReorder(order)}
            disabled={reordering}
            aria-busy={reordering}
            aria-label={`Reorder from ${order.restaurant?.name || "this restaurant"}`}
            sx={{
              ...BUTTON_RESET,
              ...FOCUS_RING,
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              height: 44,
              px: 2,
              borderRadius: RADIUS.pill,
              bgcolor: C.red,
              color: "#FFFFFF",
              fontFamily: FONT_BODY,
              fontSize: 14,
              fontWeight: 700,
              "&:hover:not(:disabled)": { bgcolor: C.redHover },
              "&:disabled": { opacity: 0.7, cursor: "default" },
            }}
          >
            {reordering ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <ReplayRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />}
            Reorder
          </Box>
        )}
      </Box>

      <ReviewDialog
        open={dialogRating > 0}
        initialRating={dialogRating}
        onClose={() => {
          setDialogRating(0);
          if (!reviewed) setPreview(0);
        }}
        orderId={order._id}
        restaurantName={order.restaurant?.name}
        onDone={(rating) => {
          setReviewed(true);
          setReviewRating(rating);
        }}
      />
    </Box>
  );
}

export default React.memo(OrderHistoryCard);
